// common/services/player/playerDetailsTables.ts
import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'
import type { ImageLike } from '../../utils/assetUrl'

/*
 * GET /players/:id/details — the admin Player Details lists, as the API sends
 * them. `suspensions` and `waivers` follow the agreed contract; the fields
 * marked optional have not been seen in a response yet.
 */

export interface PlayerDetailsFamilyMember {
  id: string
  name: string
  relationship: string
  email?: string | null
  /** E.164. */
  phone_number?: string | null
  /** `invited` or `accepted`. */
  status: string
  /** Has administrative access to the player. */
  is_primary: boolean
}

export interface PlayerDetailsAmount {
  currency: string
  /** Major units (dollars), e.g. 425.5. */
  value: number
}

export interface PlayerDetailsRegistration {
  id: string
  /** The competition's name. */
  schedule: string
  schedule_id: string | null
  /** Competition logo: an image object or URL; absent → placeholder. */
  schedule_image?: ImageLike
  division: string
  division_id?: string | null
  organization: string
  organization_id: string | null
  organization_image?: ImageLike
  status: string
  /** `YYYY-MM-DD`. */
  submitted_at: string | null
  amount: PlayerDetailsAmount | null
}

export interface PlayerDetailsRoster {
  id: string
  team: string
  team_id: string | null
  /** Team logo: an image object or URL; absent → placeholder. */
  team_image?: ImageLike
  /** The competition's name. */
  schedule: string
  schedule_id: string | null
  schedule_image?: ImageLike
  organization: string
  organization_id: string | null
  organization_image?: ImageLike
  player_number: number | string | null
  position: string | null
}

export interface PlayerDetailsSuspension {
  id: string
  reason?: string | null
  games?: number | null
  /** `YYYY-MM-DD`. */
  start_date?: string | null
  /** `YYYY-MM-DD`; null = indefinite. */
  end_date?: string | null
  organization?: string | null
  organization_id?: string | null
  status?: string | null
}

export interface PlayerDetailsWaiver {
  id: string
  /** The waiver's name. */
  waiver?: string | null
  organization?: string | null
  organization_id?: string | null
  signed_by?: string | null
  /** `YYYY-MM-DD` or ISO instant. */
  signed_at?: string | null
  status?: string | null
  /** A signed copy is on file to download. */
  has_document?: boolean
}

export interface PlayerDetailsResponse {
  family: PlayerDetailsFamilyMember[]
  registrations: PlayerDetailsRegistration[]
  rosters: PlayerDetailsRoster[]
  suspensions: PlayerDetailsSuspension[]
  waivers: PlayerDetailsWaiver[]
}

/** GET /players/:playerId/details — admin only (403 otherwise). */
export const getPlayerDetailsTables = async (
  playerId: string
): Promise<PlayerDetailsResponse> => {
  const { data } = await req(ENDPOINTS.PLAYERS.GET_PLAYER_DETAILS(playerId))
  return data as PlayerDetailsResponse
}
